import type { Song } from "@libs/wire";
import { useAuthSheet } from "@components/modals/AuthModal/useAuthSheet";
import config from "@utils/config";
import { RouterInputs, RouterOutputs, trpc } from "@utils/trpc";
import { useCallback, useEffect, useMemo } from "react";
import { useMe } from "@hooks/trpc/useMe";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useData } from "../../../runtime/provider";
import { captureException } from "@utils/captureException";
import { NoAuthError } from "@utils/NoAuthError";
import { useDownloads } from "@utils/downloadManager/hooks/useDownloads";
import { useOfflineLibrary } from "@utils/downloadManager/hooks";
import {
  usePlaylist,
  useCreatePlaylist,
  useAddSong,
  playlistQueryKey,
} from "../usePlaylists";
import type { PlaylistWithSongs } from "@libs/wire";
import {
  libraryQuery,
  libraryQueryKey,
  type LibraryQueryData,
} from "./queryFunctions";

type LibraryAddItemInput = RouterInputs["user"]["library"]["addItem"];
type LibraryAddItemOutput = RouterOutputs["user"]["library"]["addItem"];
type LibraryPlaylistItem = Extract<
  LibraryQueryData[number],
  { type: "playlist" }
>;

/**
 * Get the library, and update the cache when the library changes. The user's library is split into 4 different tables in the db: playlists, albums, artists, and songs.
 * The endpoint merges all these tables into one.
 * Fetching in the library sets caches for the catalogs, so the data is available in the collection screen without having to fetch again.
 * This hook also provides the ability to interact with the library, like marking a song as liked, or adding a playlist to the library.
 */
export const useLibrary = (
  opts: {
    refetchOnMount?: boolean | "always";
  } = {},
) => {
  const data = useData();
  const downloads = useDownloads();
  const { queryClient, utils } = data;
  const {
    query: { data: userData },
  } = useMe();
  const openAuthSheet = useAuthSheet();

  const rawQuery = useQuery({
    ...libraryQuery(data),
    refetchOnMount: true, // Only refetch if data is stale (respects staleTime)
    ...opts,
  });

  // Live read of the user's downloaded items. Compose with the durable
  // server library so a downloaded item's image is shown from local disk
  // (rather than the remote CDN URL, which would 404 on bad internet).
  // This replaces the substitution that mergeOfflineAndOnlineData did
  // pre-Phase-1, but reactively — when a new download completes,
  // useOfflineLibrary's react-query subscription fires and the composed
  // value updates without waiting for a library refetch.
  const { offlineLibrary } = useOfflineLibrary();
  const offlineMap = useMemo(
    () =>
      offlineLibrary ? new Map(offlineLibrary.map((it) => [it.id, it])) : null,
    [offlineLibrary],
  );

  // Side effect that mergeOfflineAndOnlineData used to fire: for each item
  // present in both the server library and offline_collections, refresh
  // the offline copy's metadata from the latest remote shape (titles,
  // images, etc.). Keeps the offline snapshot from drifting after server
  // edits.
  useEffect(() => {
    if (!rawQuery.data || !offlineMap) return;
    for (const item of rawQuery.data) {
      if (offlineMap.has(item.id)) {
        downloads.updateOfflineCollection(item.id).catch(console.error);
      }
    }
  }, [rawQuery.data, offlineMap, downloads]);

  const composedData = useMemo(() => {
    if (!rawQuery.data) return rawQuery.data;
    if (!offlineMap || offlineMap.size === 0) return rawQuery.data;
    return rawQuery.data.map((item) => {
      const offline = offlineMap.get(item.id);
      if (!offline) return item;
      // Substitute image fields with the offline (local-path) version.
      // Other fields stay server-truth; downloads ⊂ library, so the
      // server is authoritative on visibility, ordering, tags, etc.
      if (item.type === "playlist" && offline.type === "playlist") {
        return { ...item, images: offline.images };
      }
      if (item.type === "album" && offline.type === "album") {
        return { ...item, image: offline.image };
      }
      if (item.type === "artist" && offline.type === "artist") {
        return { ...item, image: offline.image };
      }
      return item;
    });
  }, [rawQuery.data, offlineMap]);

  // Spread the raw query and override `data` with the composed value.
  // Methods (`refetch`, etc.) and other status flags pass through.
  const query = useMemo(
    () => ({ ...rawQuery, data: composedData }),
    [rawQuery, composedData],
  ) as typeof rawQuery;

  const getLibraryQueryKey = () => libraryQueryKey();

  const likedSongsPlaylist = usePlaylist(
    userData?.id ? `liked_${userData?.id}` : "",
    { disableToast: true },
  );

  const { addSong } = useAddSong();

  const { createPlaylist } = useCreatePlaylist();

  const addToLibraryMutation = useMutation<
    LibraryAddItemOutput,
    Error,
    LibraryAddItemInput
  >({
    mutationFn: (input) => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("addToLibraryMutation mutationFn");
      }
      return data.api.user.library.addItem.mutate(input);
    },
  });

  const removeFromLibraryMutation = trpc.user.library.removeItem.useMutation();

  const isLiked = useCallback(
    ({
      id,
      type,
    }: {
      id?: string;
      type: "album" | "playlist" | "song" | "artist";
    }) => {
      if (!query.data) return false;
      if (!id) return false;
      if (type === "song") {
        return !!query.data?.find(
          (item) =>
            item.type === "playlist" &&
            item.id.startsWith("liked_") &&
            item.songIds?.find((songId: string) => songId === id),
        );
      }
      return !!query.data?.find((item) => item.id === id && item.type === type);
    },
    [query.data],
  );

  const mutation = (
    id: string,
    type: "album" | "playlist" | "artist",
    liked: boolean,
  ) => {
    console.log(`mutation called with: ${id} and ${type} - liked: ${liked}`);

    if (liked) {
      removeFromLibraryMutation
        .mutateAsync(
          { id, type },
          {
            onSuccess: () => {
              queryClient
                .invalidateQueries({
                  queryKey: getLibraryQueryKey(),
                })
                .catch(console.error);
            },
          },
        )
        .catch((error) => {
          captureException(error, {
            channel: "error",
            area: "library",
            operation: "remove-collection-from-library",
            tags: { type, id },
          });
        });
    } else {
      addToLibraryMutation
        .mutateAsync(
          { id, type },
          {
            onSuccess: () => {
              queryClient
                .invalidateQueries({
                  queryKey: getLibraryQueryKey(),
                })
                .catch(console.error);
            },
          },
        )
        .catch((error) => {
          captureException(error, {
            channel: "error",
            area: "library",
            operation: "add-collection-to-library",
            tags: { type, id },
          });
        });
    }
  };

  const toggleCollectionLike = (
    id: string,
    type: "album" | "playlist" | "artist",
  ) => {
    if (!data.auth.currentUser()) {
      openAuthSheet();
      return;
    }
    const liked = isLiked({ id, type });

    //this part always happens immediately - we want the ui to update immediately
    // Optimistic updates write through to SQLite via setQueryDataDurable so
    // a follow-up refetch failure can't revert them (see review P2 #1).
    if (liked) {
      data.cache.setQueryDataDurable<typeof query.data>(
        queryClient,
        getLibraryQueryKey(),
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.filter((item) => item.id !== id);
        },
      );
    } else {
      const oldLibraryData = query.data;
      if (type === "playlist") {
        // Durable-cache row is a single PlaylistWithSongs (post-Phase-2.3),
        // not an array — read directly from queryClient by key.
        const playlistData = queryClient.getQueryData<PlaylistWithSongs>(
          playlistQueryKey(id),
        );
        if (playlistData) {
          data.cache.setQueryDataDurable<typeof query.data>(
            queryClient,
            getLibraryQueryKey(),
            [
              ...(oldLibraryData || []),
              {
                type: "playlist",
                ...playlistData,
                images: playlistData.images ?? [],
                privateSongIds: playlistData.privateSongIds ?? [],
                songIds: [] as string[],
                created_at: new Date().toISOString(),
              },
            ],
          );
        }
      }
      if (type === "album") {
        const albumData = utils.catalog.albums.getById.getData([id])?.at(0);
        if (albumData) {
          data.cache.setQueryDataDurable<typeof query.data>(
            queryClient,
            getLibraryQueryKey(),
            [
              ...(oldLibraryData || []),
              {
                type: "album",
                ...albumData,
                privateSongIds: albumData?.privateSongIds ?? [],
                created_at: new Date().toISOString(),
              },
            ],
          );
        }
      }
    }
    console.log(
      `toggleCollectionLike called with: ${id} and ${type} - liked: ${liked}`,
    );
    //the actual mutation happens after a delay, so the ui doesn't get reset by the calls if the user clicks fast
    mutation(id, type, liked);
  };

  const toggleSongLike = (song?: Song) => {
    if (!userData) return;
    if (!data.auth.currentUser()) {
      openAuthSheet();
      return;
    }
    if (!song) return;
    const liked = isLiked({ id: song.id, type: "song" });

    //update the ui immediately
    if (liked) {
      console.log(`Removing ${song.id} from liked songs`);
      data.cache.setQueryDataDurable<typeof query.data>(
        queryClient,
        getLibraryQueryKey(),
        (oldData) =>
          oldData?.map((item) =>
            item.type === "playlist" && item.id.startsWith("liked_")
              ? {
                  ...item,
                  songIds:
                    item.songIds?.filter((s: string) => {
                      return s !== song.id;
                    }) ?? [],
                  privateSongIds:
                    item.privateSongIds?.filter((s: string) => s !== song.id) ??
                    [],
                }
              : item,
          ),
      );

      likedSongsPlaylist.removeSong
        .mutateAsync({
          id: `liked_${userData.id}`,
          songId: song.id,
        })
        .catch((error) => {
          captureException(error, {
            channel: "error",
            area: "library",
            operation: "remove-liked-song",
            tags: { songId: song.id },
          });
        });
    } else {
      console.log(`Adding ${song.id} to liked songs`);
      console.log("libraryQueryKey", getLibraryQueryKey());
      const likedSongs = query.data?.find((x) => x.id.startsWith("liked_"));
      if (likedSongs) {
        data.cache.setQueryDataDurable<typeof query.data>(
          queryClient,
          getLibraryQueryKey(),
          (oldData) => {
            if (oldData) {
              return oldData.map((item) => {
                if (item.type === "playlist" && item.id.startsWith("liked_")) {
                  const privateSongIds = item.privateSongIds ?? [];
                  return {
                    ...item,
                    songIds: [song.id, ...item.songIds],
                    privateSongIds: song.private
                      ? [song.id, ...privateSongIds]
                      : privateSongIds,
                  };
                }
                return item;
              });
            }
          },
        );

        addSong([`liked_${userData.id}`], song);
      } else {
        // Don't do an optimistic update here since createPlaylist already handles it
        // This prevents duplicate entries when playlists are being imported concurrently
        createPlaylist({
          id: `liked_${userData.id}`,
          title: "Liked Songs",
          visibility: "private",
          image: `${config.CDN_URL}/images/liked_songs_v1`,
          songToAdd: song,
        });
      }
    }
  };

  const myPlaylists = useMemo<LibraryPlaylistItem[]>(
    () =>
      query.data?.filter(
        (item): item is LibraryPlaylistItem =>
          item.type === "playlist" &&
          item.id !== "discover_daily" &&
          !item.id.startsWith("liked_") &&
          item.user?.id === userData?.id,
      ) || [],
    [query.data, userData?.id],
  );

  return {
    query,
    toggleCollectionLike,
    isLiked,
    toggleSongLike,
    myPlaylists,
  };
};
